import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  Button,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon from '@mui/icons-material/People';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EventIcon from '@mui/icons-material/Event';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ReplyIcon from '@mui/icons-material/Reply';
import ChatIcon from '@mui/icons-material/Chat';
import { dashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Format timestamp
const formatTime = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString();
};

// Stat Card Component
const StatCard = ({ icon, label, value, color = 'primary', onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s ease',
      '&:hover': onClick
        ? {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }
        : {},
    }}
  >
    <CardContent>
      <Box display="flex" alignItems="center" gap={2}>
        <Avatar
          sx={{
            bgcolor: `${color}.light`,
            color: `${color}.main`,
            width: 48,
            height: 48,
          }}
        >
          {icon}
        </Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700} color="secondary.dark">
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Activity Item Component
const ActivityItem = ({ activity, onClick }) => {
  const getActivityIcon = () => {
    switch (activity.type) {
      case 'user_reply':
        return <ReplyIcon sx={{ fontSize: 18 }} />;
      case 'new_thread':
        return <ChatIcon sx={{ fontSize: 18 }} />;
      default:
        return <InboxIcon sx={{ fontSize: 18 }} />;
    }
  };

  const getActivityText = () => {
    switch (activity.type) {
      case 'user_reply':
        return `${activity.user_first_name} sent a reply`;
      case 'new_thread':
        return `New thread from ${activity.user_first_name}`;
      default:
        return activity.type;
    }
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1.5,
        px: 2,
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <Avatar
        sx={{
          width: 36,
          height: 36,
          bgcolor: 'primary.light',
          color: 'primary.dark',
        }}
      >
        {getActivityIcon()}
      </Avatar>
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" fontWeight={500} noWrap>
          {getActivityText()}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary">
            {activity.reference_id}
          </Typography>
          {activity.message_type && (
            <Chip
              label={activity.message_type.replace(/_/g, ' ')}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: 10 }}
            />
          )}
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary">
        {formatTime(activity.timestamp)}
      </Typography>
    </Box>
  );
};

function Dashboard() {
  const navigate = useNavigate();
  const { user, sessionKey } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dashboardApi.getStats();
      setStats(data);
    } catch (err) {
      setError('Failed to load dashboard stats. Please try again.');
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset stats and refetch when user changes or new login session starts
  useEffect(() => {
    setStats(null);
    setLoading(true);
    fetchStats();
  }, [user?.id, sessionKey, fetchStats]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Welcome Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="secondary.dark">
            Welcome back, {user?.name?.split(' ')[0] || 'Nutritionist'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Here's what's happening with your nutritionist queue today.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchStats}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Grid */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<InboxIcon />}
            label="Needs Your Response"
            value={stats?.threads?.awaiting_nutritionist || 0}
            color="warning"
            onClick={() => navigate('/queue?status=AWAITING_NUTRITIONIST')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<HourglassEmptyIcon />}
            label="Awaiting User"
            value={stats?.threads?.awaiting_user || 0}
            color="info"
            onClick={() => navigate('/queue?status=AWAITING_USER')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<CheckCircleIcon />}
            label="Closed Today"
            value={stats?.threads?.closed_today || 0}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<CheckCircleIcon />}
            label="Total Closed"
            value={stats?.threads?.total_closed || 0}
            color="secondary"
          />
        </Grid>
      </Grid>

      {/* User Stats */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<PeopleIcon />}
            label="Users with Access"
            value={stats?.users?.with_nutritionist_access || 0}
            color="primary"
            onClick={() => navigate('/users')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<WarningAmberIcon />}
            label="Complex Cases"
            value={stats?.users?.complex_cases || 0}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<EventIcon />}
            label="Pending Check-ins"
            value={stats?.users?.pending_checkins || 0}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Recent Activity & Quick Actions */}
      <Grid container spacing={3}>
        {/* Recent Activity */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                  Recent Activity
                </Typography>
                <Button
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/queue')}
                  size="small"
                >
                  View Queue
                </Button>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {stats?.recent_activity?.length > 0 ? (
                stats.recent_activity.map((activity, idx) => (
                  <ActivityItem
                    key={idx}
                    activity={activity}
                    onClick={() => navigate(`/thread/${activity.reference_id}`)}
                  />
                ))
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography variant="body2" color="text.secondary">
                    No recent activity
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Quick Actions
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  color="warning"
                  fullWidth
                  onClick={() => navigate('/queue?status=AWAITING_NUTRITIONIST')}
                  startIcon={<InboxIcon />}
                >
                  View Pending Queue ({stats?.threads?.awaiting_nutritionist || 0})
                </Button>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/users?has_open_thread=false')}
                  startIcon={<EventIcon />}
                >
                  Create Weekly Check-in
                </Button>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/queue')}
                  startIcon={<InboxIcon />}
                >
                  View All Threads
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Tips Card */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Tips
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Threads marked with "Needs Your Response" should be prioritized.
                Users are waiting for your expert guidance.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;
